import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import notificationService from '../services/notificationService';
import { useAuth } from './AuthContext';
import { useAppContext } from './AppContext';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionText?: string;
  category?: 'system' | 'user' | 'security' | 'update' | 'approval';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  entityType?: string;
  entityId?: string;
  actionData?: Record<string, any>;
  sender?: {
    id: string;
    name: string;
    email: string;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAsUnread: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  refetchNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { state: appState } = useAppContext();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  }, []);

  const markAsUnread = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsUnread(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: false }
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as unread:', err);
      setError('Failed to mark notification as unread');
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      // Get current stage and app context for filtering
      const selectedStageId = localStorage.getItem('selectedStageId');
      const currentApp = appState.currentApp;
      
      const params: any = {};
      if (currentApp === 'default' && selectedStageId) {
        params.stageId = selectedStageId;
      }
      if (currentApp === 'codevault') {
        params.appName = 'codevault';
      } else if (currentApp !== 'default') {
        params.appName = currentApp;
      }
      
      const result = await notificationService.markAllAsRead(params);
      if (result.success) {
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, read: true }))
        );
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Failed to mark all notifications as read');
    }
  }, [appState.currentApp]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  }, []);

  const clearAllNotifications = useCallback(async () => {
    try {
      // Get current stage and app context for filtering
      const selectedStageId = localStorage.getItem('selectedStageId');
      const currentApp = appState.currentApp;
      
      const params: any = {};
      if (currentApp === 'default' && selectedStageId) {
        params.stageId = selectedStageId;
      }
      if (currentApp === 'codevault') {
        params.appName = 'codevault';
      } else if (currentApp !== 'default') {
        params.appName = currentApp;
      }
      
      const result = await notificationService.clearAllNotifications(params);
      if (result.success) {
        setNotifications([]);
      }
    } catch (err) {
      console.error('Error clearing notifications:', err);
      setError('Failed to clear notifications');
    }
  }, [appState.currentApp]);

  const refetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Get current stage from localStorage and current app from context
      const selectedStageId = localStorage.getItem('selectedStageId');
      const currentApp = appState.currentApp;
      
      // Prepare filters based on context
      const filters: any = {};
      
      // Add stage filter only for default (stage-based) app context
      if (currentApp === 'default' && selectedStageId) {
        filters.stageId = selectedStageId;
      }
      
      // Add app filter - for CodeVault app, show only codevault notifications
      if (currentApp === 'codevault') {
        filters.appName = 'codevault';
      } else if (currentApp !== 'default') {
        // For other specific apps, filter by app name
        filters.appName = currentApp;
      }
      // For default app, show all notifications (no app filter)
      
      const response = await notificationService.getNotifications(filters);
      if (response.success) {
        const transformedNotifications: Notification[] = response.data.map((item: any) => ({
          id: item.id,
          title: item.title,
          message: item.message,
          type: item.type,
          timestamp: new Date(item.timestamp),
          read: item.read,
          actionUrl: item.actionUrl,
          actionText: item.actionText,
          category: item.category,
          priority: item.priority,
          entityType: item.entityType,
          entityId: item.entityId,
          actionData: item.actionData,
          sender: item.sender
        }));
        setNotifications(transformedNotifications);
      }
    } catch (err) {
      setError('Failed to fetch notifications');
      console.error('Error fetching notifications:', err);      
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, appState.currentApp]);

  useEffect(() => {
    if (isAuthenticated) {
      refetchNotifications();
    } else {
      setNotifications([]);
    }
  }, [isAuthenticated, refetchNotifications, appState.currentApp]);

  // Listen for stage selection changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'selectedStageId' && isAuthenticated) {
        refetchNotifications();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated, refetchNotifications]);

  const contextValue = useMemo<NotificationContextType>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      refetchNotifications,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      refetchNotifications,
    ]
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);

  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }

  return context;
};